import { ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { Router } from 'vue-router'
import { aiApi, bookApi } from '@/api'
import type { Book } from '@/types/user'
import type { ReadingChapter } from '@/types/reading'

type UseReadingPublishOptions = {
  book: Ref<Book>
  chapter: Ref<ReadingChapter>
  router: Router
  genreLabel: ComputedRef<string>
  isPersonalOriginalCreation: ComputedRef<boolean>
  showCreatorAchievement: Ref<boolean>
}

export const useReadingPublish = (options: UseReadingPublishOptions) => {
  const showPublishDialog = ref(false)
  const titleOptions = ref<string[]>([])
  const publishingTitle = ref('')
  const coverOptions = ref<string[]>([])
  const selectedCoverUrl = ref('')
  const isPreparingPublish = ref(false)
  const isPublishing = ref(false)
  const publishError = ref('')

  const buildTitleOptions = () => {
    const baseTitle = options.book.value.title.replace(/^我的/, '').trim() || '未命名故事'
    const suffixes = ['新章', '另一条命运线', '续写版']
    return suffixes.map(suffix => `${baseTitle}${suffix}`).slice(0, 3)
  }

  const generatePublishTitleOptions = async () => {
    try {
      isPreparingPublish.value = true
      const titleRes = await aiApi.generateBookTitleOptions({
        originalTitle: options.book.value.title,
        description: options.book.value.description || options.chapter.value.content.slice(0, 120),
        genre: options.genreLabel.value,
        numOptions: 3
      })
      if (titleRes.code === 200 && titleRes.data?.titles?.length) {
        titleOptions.value = titleRes.data.titles
        publishingTitle.value = titleOptions.value[0] || publishingTitle.value
      }
    } catch (err) {
      console.warn('书名候选生成失败:', err)
    } finally {
      isPreparingPublish.value = false
    }
  }

  const generatePublishCoverOptions = async () => {
    try {
      isPreparingPublish.value = true
      const res = await aiApi.generateCoverOptions({
        novelTitle: publishingTitle.value || options.book.value.title,
        novelDescription: options.book.value.description || options.chapter.value.content.slice(0, 120),
        genre: options.genreLabel.value,
        numOptions: 3
      })
      if (res.code === 200 && res.data?.options?.length) {
        const generatedCovers = res.data.options.map(option => option.imageUrl).filter(Boolean)
        if (generatedCovers.length) {
          coverOptions.value = generatedCovers
          selectedCoverUrl.value = coverOptions.value[0] || selectedCoverUrl.value
        }
      }
    } catch (err) {
      console.warn('封面候选生成失败:', err)
    } finally {
      isPreparingPublish.value = false
    }
  }

  const openPublishDialog = async () => {
    options.showCreatorAchievement.value = false
    showPublishDialog.value = true
    publishError.value = ''

    if (options.isPersonalOriginalCreation.value) {
      titleOptions.value = [options.book.value.title].filter(Boolean)
      publishingTitle.value = options.book.value.title
      coverOptions.value = [options.book.value.coverUrl].filter(Boolean)
      selectedCoverUrl.value = coverOptions.value[0] || ''
      return
    }

    if (titleOptions.value.length === 0) {
      titleOptions.value = buildTitleOptions()
      publishingTitle.value = titleOptions.value[0] || options.book.value.title
      await generatePublishTitleOptions()
    }

    if (coverOptions.value.length === 0) {
      coverOptions.value = [options.book.value.coverUrl].filter(Boolean)
      selectedCoverUrl.value = coverOptions.value[0] || ''
      await generatePublishCoverOptions()
    }
  }

  const publishBook = async () => {
    if (!publishingTitle.value.trim() || !selectedCoverUrl.value) return

    try {
      isPublishing.value = true
      publishError.value = ''
      const res = await bookApi.publishPrivateBook(options.book.value.id, {
        title: publishingTitle.value.trim(),
        coverUrl: selectedCoverUrl.value,
        description: options.book.value.description
      })

      if (res.code === 200 && res.data?.book) {
        showPublishDialog.value = false
        alert('已经提交审核，通过后会进入公共书库')
        options.router.push(`/books/${res.data.book.id}`)
      } else {
        throw new Error(res.message || '上架失败')
      }
    } catch (err: any) {
      console.error('上架失败:', err)
      publishError.value = err.message || '上架失败，请稍后重试'
    } finally {
      isPublishing.value = false
    }
  }

  return {
    showPublishDialog,
    titleOptions,
    publishingTitle,
    coverOptions,
    selectedCoverUrl,
    isPreparingPublish,
    isPublishing,
    publishError,
    generatePublishTitleOptions,
    generatePublishCoverOptions,
    openPublishDialog,
    publishBook
  }
}
